import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  onSnapshot,
  Timestamp,
  Query,
  DocumentData
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../firebase';
import {
  CustomerInquiry,
  InquiryFormData,
  InquiryFilterOptions,
  InquiryStatistics,
  AdminResponseData,
  MessageData
} from '../types/inquiry';

interface UseInquiryReturn {
  inquiries: CustomerInquiry[];
  loading: boolean;
  error: string | null;
  statistics: InquiryStatistics | null;

  // CRUD 작업
  createInquiry: (formData: InquiryFormData) => Promise<string>;
  updateInquiry: (id: string, data: Partial<CustomerInquiry>) => Promise<void>;
  deleteInquiry: (id: string) => Promise<void>;

  // 특정 문의 조회
  getInquiry: (id: string) => Promise<CustomerInquiry | null>;

  // 관리자 기능
  respondToInquiry: (id: string, response: AdminResponseData) => Promise<void>;
  addMessage: (id: string, message: MessageData) => Promise<void>;

  // 필터링 및 검색
  filterInquiries: (filters: InquiryFilterOptions) => void;
  searchInquiries: (searchTerm: string) => void;

  // 리프레시
  refreshInquiries: () => void;

  // 읽음 상태 관리
  markAsRead: (inquiryId: string) => Promise<void>;
  hasUnreadMessages: (inquiry: CustomerInquiry, userUid: string) => boolean;
}

export const useInquiry = (options?: {
  limit?: number;
  realtime?: boolean;
  userUid?: string; // 관리자용: 특정 사용자의 문의만 조회
  adminMode?: boolean; // 관리자 모드: 모든 문의 조회
}): UseInquiryReturn => {
  const [inquiries, setInquiries] = useState<CustomerInquiry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<InquiryFilterOptions>({});
  const [searchTerm, setSearchTerm] = useState('');

  // 현재 사용자 정보
  const currentUser = auth.currentUser;

  // Firestore timestamp를 Date로 변환하는 헬퍼 함수
  const convertTimestampToDate = (data: any): Omit<CustomerInquiry, 'id'> => {
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      adminResponse: data.adminResponse ? {
        ...data.adminResponse,
        respondedAt: data.adminResponse.respondedAt?.toDate() || new Date()
      } : undefined,
      messages: data.messages?.map((msg: any) => ({
        ...msg,
        createdAt: msg.createdAt?.toDate() || new Date()
      })) || [],
      lastReadBy: data.lastReadBy ? Object.keys(data.lastReadBy).reduce((acc, uid) => {
        acc[uid] = data.lastReadBy[uid]?.toDate() || new Date();
        return acc;
      }, {} as { [key: string]: Date }) : undefined
    };
  };

  // 쿼리 빌드 함수
  const buildQuery = useCallback((): Query<DocumentData> => {
    let q = query(collection(db, 'customer_inquiries'));

    // 사용자별 필터링
    if (options?.userUid) {
      // 관리자가 특정 사용자의 문의를 조회하는 경우
      q = query(q, where('userUid', '==', options.userUid));
    } else if (!options?.adminMode && currentUser) {
      // 일반 사용자는 자신의 문의만 조회 (관리자 모드가 아닌 경우)
      q = query(q, where('userUid', '==', currentUser.uid));
    }
    // 관리자 모드인 경우 모든 문의를 조회 (필터링 없음)

    // 필터 적용
    if (filters.status && filters.status !== 'all') {
      q = query(q, where('status', '==', filters.status));
    }

    if (filters.category && filters.category !== 'all') {
      q = query(q, where('category', '==', filters.category));
    }

    // 정렬
    const sortField = filters.sortBy || 'createdAt';
    const sortDirection = filters.sortOrder || 'desc';
    q = query(q, orderBy(sortField, sortDirection));

    // 제한
    if (options?.limit) {
      q = query(q, limit(options.limit));
    }

    return q;
  }, [filters, options?.userUid, options?.limit, options?.adminMode, currentUser]);

  // 문의 목록 로드
  const loadInquiries = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      const q = buildQuery();
      const querySnapshot = await getDocs(q);

      let results: CustomerInquiry[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...convertTimestampToDate(doc.data())
      }));

      // 검색어 필터링 (클라이언트 사이드)
      if (searchTerm) {
        const lowercaseSearch = searchTerm.toLowerCase();
        results = results.filter(inquiry =>
          inquiry.subject.toLowerCase().includes(lowercaseSearch) ||
          inquiry.content.toLowerCase().includes(lowercaseSearch) ||
          inquiry.userInfo.name.toLowerCase().includes(lowercaseSearch) ||
          inquiry.userInfo.organization.toLowerCase().includes(lowercaseSearch)
        );
      }

      setInquiries(results);
    } catch (err) {
      console.error('문의 목록 로드 실패:', err);
      setError(err instanceof Error ? err.message : '문의 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [buildQuery, searchTerm, currentUser]);

  // 실시간 구독
  useEffect(() => {
    if (!options?.realtime || !currentUser) {
      loadInquiries();
      return;
    }

    const q = buildQuery();
    const unsubscribe = onSnapshot(q,
      (querySnapshot) => {
        let results: CustomerInquiry[] = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...convertTimestampToDate(doc.data())
        }));

        // 검색어 필터링
        if (searchTerm) {
          const lowercaseSearch = searchTerm.toLowerCase();
          results = results.filter(inquiry =>
            inquiry.subject.toLowerCase().includes(lowercaseSearch) ||
            inquiry.content.toLowerCase().includes(lowercaseSearch) ||
            inquiry.userInfo.name.toLowerCase().includes(lowercaseSearch) ||
            inquiry.userInfo.organization.toLowerCase().includes(lowercaseSearch)
          );
        }

        setInquiries(results);
        setLoading(false);
      },
      (err) => {
        console.error('실시간 문의 구독 실패:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [buildQuery, searchTerm, options?.realtime, currentUser, loadInquiries]);

  // 첨부파일 업로드
  const uploadAttachments = async (files: File[]): Promise<string[]> => {
    const uploadPromises = files.map(async (file) => {
      const fileName = `inquiries/${currentUser?.uid}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      return getDownloadURL(snapshot.ref);
    });

    return Promise.all(uploadPromises);
  };

  // 문의 생성
  const createInquiry = useCallback(async (formData: InquiryFormData): Promise<string> => {
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setLoading(true);
      setError(null);

      // 사용자 프로필 조회
      const userProfileDoc = await getDoc(doc(db, 'user_profiles', currentUser.uid));
      if (!userProfileDoc.exists()) {
        throw new Error('사용자 프로필을 찾을 수 없습니다.');
      }

      const userProfile = userProfileDoc.data();
      let attachmentUrls: string[] = [];

      // 첨부파일 업로드
      if (formData.attachments && formData.attachments.length > 0) {
        attachmentUrls = await uploadAttachments(formData.attachments);
      }

      const inquiryData = {
        userUid: currentUser.uid,
        userInfo: {
          name: userProfile.name,
          email: userProfile.email,
          organization: userProfile.organization
        },
        category: formData.category,
        subject: formData.subject,
        content: formData.content,
        priority: 'normal' as const, // 사용자 생성 시 기본값은 normal, 관리자가 나중에 변경 가능
        status: 'pending' as const,
        attachments: attachmentUrls.length > 0 ? attachmentUrls : undefined,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'customer_inquiries'), inquiryData);

      // 목록 새로고침
      if (!options?.realtime) {
        loadInquiries();
      }

      return docRef.id;
    } catch (err) {
      console.error('문의 생성 실패:', err);
      const errorMessage = err instanceof Error ? err.message : '문의 생성에 실패했습니다.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentUser, options?.realtime, loadInquiries, uploadAttachments]);

  // 문의 수정
  const updateInquiry = useCallback(async (id: string, data: Partial<CustomerInquiry>): Promise<void> => {
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      setLoading(true);
      setError(null);

      const updateData: any = {
        ...data,
        updatedAt: Timestamp.now()
      };

      // Date를 Timestamp로 변환
      if (data.adminResponse?.respondedAt) {
        updateData.adminResponse = {
          ...data.adminResponse,
          respondedAt: Timestamp.fromDate(data.adminResponse.respondedAt)
        };
      }

      if (data.messages) {
        updateData.messages = data.messages.map(msg => ({
          ...msg,
          createdAt: Timestamp.fromDate(msg.createdAt)
        }));
      }

      if (data.lastReadBy) {
        updateData.lastReadBy = Object.keys(data.lastReadBy).reduce((acc, uid) => {
          acc[uid] = Timestamp.fromDate(data.lastReadBy![uid]);
          return acc;
        }, {} as any);
      }

      await updateDoc(doc(db, 'customer_inquiries', id), updateData);

      // 목록 새로고침
      if (!options?.realtime) {
        loadInquiries();
      }
    } catch (err) {
      console.error('문의 수정 실패:', err);
      const errorMessage = err instanceof Error ? err.message : '문의 수정에 실패했습니다.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentUser, options?.realtime, loadInquiries]);

  // 문의 삭제 (실제로는 사용되지 않음 - 보안 규칙에서 차단)
  const deleteInquiry = useCallback(async (id: string): Promise<void> => {
    throw new Error('문의 삭제는 지원되지 않습니다.');
  }, []);

  // 특정 문의 조회
  const getInquiry = useCallback(async (id: string): Promise<CustomerInquiry | null> => {
    try {
      const docSnap = await getDoc(doc(db, 'customer_inquiries', id));

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...convertTimestampToDate(docSnap.data())
        } as CustomerInquiry;
      }

      return null;
    } catch (err) {
      console.error('문의 조회 실패:', err);
      setError(err instanceof Error ? err.message : '문의를 불러오는데 실패했습니다.');
      return null;
    }
  }, []);

  // 관리자 답변
  const respondToInquiry = useCallback(async (id: string, response: AdminResponseData): Promise<void> => {
    const responseData = {
      ...response,
      respondedAt: new Date()
    };

    await updateInquiry(id, {
      adminResponse: responseData,
      status: 'answered'
    });
  }, [updateInquiry]);

  // 메시지 추가
  const addMessage = useCallback(async (id: string, message: MessageData): Promise<void> => {
    const currentInquiry = await getInquiry(id);
    if (!currentInquiry) {
      throw new Error('문의를 찾을 수 없습니다.');
    }

    const newMessage = {
      id: `msg_${Date.now()}`,
      ...message,
      createdAt: new Date()
    };

    const updatedMessages = [...(currentInquiry.messages || []), newMessage];

    await updateInquiry(id, {
      messages: updatedMessages
    });
  }, [getInquiry, updateInquiry]);

  // 필터링
  const filterInquiries = useCallback((newFilters: InquiryFilterOptions) => {
    setFilters(newFilters);
  }, []);

  // 검색
  const searchInquiries = useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  }, []);

  // 새로고침
  const refreshInquiries = useCallback(() => {
    loadInquiries();
  }, [loadInquiries]);

  // 읽음 상태 표시
  const markAsRead = useCallback(async (inquiryId: string): Promise<void> => {
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }

    try {
      const currentInquiry = await getInquiry(inquiryId);
      if (!currentInquiry) {
        throw new Error('문의를 찾을 수 없습니다.');
      }

      const updatedLastReadBy = {
        ...currentInquiry.lastReadBy,
        [currentUser.uid]: new Date()
      };

      await updateInquiry(inquiryId, {
        lastReadBy: updatedLastReadBy
      });
    } catch (err) {
      console.error('읽음 상태 업데이트 실패:', err);
      throw err;
    }
  }, [currentUser, getInquiry, updateInquiry]);

  // 읽지 않은 메시지가 있는지 확인
  const hasUnreadMessages = useCallback((inquiry: CustomerInquiry, userUid: string): boolean => {
    const lastReadTime = inquiry.lastReadBy?.[userUid];

    // 읽음 기록이 없으면 새 메시지가 있는 것으로 간주
    if (!lastReadTime) {
      // 관리자 답변이 있거나 추가 메시지가 있으면 읽지 않은 메시지가 있음
      return !!(inquiry.adminResponse || (inquiry.messages && inquiry.messages.length > 0));
    }

    // 마지막 읽음 시간 이후의 메시지가 있는지 확인
    const hasNewAdminResponse = !!(inquiry.adminResponse &&
      inquiry.adminResponse.respondedAt.getTime() > lastReadTime.getTime());

    const hasNewMessages = !!(inquiry.messages && inquiry.messages.some(msg =>
      msg.createdAt.getTime() > lastReadTime.getTime() && msg.senderUid !== userUid
    ));

    return hasNewAdminResponse || hasNewMessages;
  }, []);

  // 통계 계산
  const statistics = useMemo((): InquiryStatistics | null => {
    if (inquiries.length === 0) return null;

    const stats: InquiryStatistics = {
      total: inquiries.length,
      pending: inquiries.filter(i => i.status === 'pending').length,
      answered: inquiries.filter(i => i.status === 'answered').length,
      closed: inquiries.filter(i => i.status === 'closed').length,
      byCategory: {
        general: inquiries.filter(i => i.category === 'general').length,
        portville: inquiries.filter(i => i.category === 'portville').length,
        parking: inquiries.filter(i => i.category === 'parking').length,
        facility: inquiries.filter(i => i.category === 'facility').length,
        account: inquiries.filter(i => i.category === 'account').length
      }
    };

    return stats;
  }, [inquiries]);

  return {
    inquiries,
    loading,
    error,
    statistics,
    createInquiry,
    updateInquiry,
    deleteInquiry,
    getInquiry,
    respondToInquiry,
    addMessage,
    filterInquiries,
    searchInquiries,
    refreshInquiries,
    markAsRead,
    hasUnreadMessages
  };
};